/** Google Places API (New) — autocomplete.
 *  1) Si existe VITE_GOOGLE_PLACES_API_KEY → llamada directa a Google.
 *  2) Si no → POST /api/geo/places-autocomplete (clave solo en servidor, misma que app móvil).
 */

import api from "../axiosConfig";

const URL = "https://places.googleapis.com/v1/places:autocomplete";

/** Países habituales (direcciones fuera de España siguen pudiendo buscarse). */
const INCLUDED_REGION_CODES = ["es", "gb", "fr", "de", "pt", "it"];

/** Oficina / fallback si el navegador no da ubicación (mismo criterio que antes). */
const PLACES_HOME_CENTER = { latitude: 38.84084, longitude: 0.10569 };

/** Radio ~“aquí” en Maps (la API Places no recibe el viewport del mapa, solo círculo/rectángulo). */
const PLACES_MAPS_LIKE_BIAS_RADIUS_M = 28000;

/** Restringir calles a la población elegida (geocentro vía `/api/geo/localidad-centro`). */
export const PLACES_LOCALITY_RESTRICTION_RADIUS_M = 11500;

/** Segunda petición (refuerzo muy local) solo cerca de casa VitalCasa — ver `nearOfficeKm`. */
const PLACES_LOCAL_RESTRICTION_RADIUS_M = 12000;
const PLACES_NEAR_OFFICE_KM = 38;

let mapsLikeBiasPromise = null;

/**
 * Igual que Maps con el mapa centrado en ti: usa Geolocation API si el usuario permite;
 * si no, oficina Dénia. Una sola petición de permiso por carga de página (promesa compartida).
 */
export function resolveMapsLikeBiasCenter() {
  if (typeof window === "undefined" || !navigator?.geolocation) {
    return Promise.resolve(PLACES_HOME_CENTER);
  }
  if (!mapsLikeBiasPromise) {
    mapsLikeBiasPromise = new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
        },
        () => resolve(PLACES_HOME_CENTER),
        { enableHighAccuracy: false, maximumAge: 300_000, timeout: 4000 },
      );
    });
  }
  return mapsLikeBiasPromise;
}

function distKm(a, b) {
  const R = 6371;
  const dLat = ((b.latitude - a.latitude) * Math.PI) / 180;
  const dLng = ((b.longitude - a.longitude) * Math.PI) / 180;
  const la1 = (a.latitude * Math.PI) / 180;
  const la2 = (b.latitude * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(x)));
}

export function getPlacesApiKey() {
  return (import.meta.env.VITE_GOOGLE_PLACES_API_KEY || "").trim();
}

/** Misma petición que en el spec original; Google puede devolver `text` como objeto o anidado. */
function displayTextFromPlacePrediction(p) {
  if (!p) return "";
  const t = p.text;
  if (typeof t === "string") return t.trim();
  if (t?.text) return String(t.text).trim();
  const main = p.structuredFormat?.mainText?.text;
  const sec = p.structuredFormat?.secondaryText?.text;
  if (main && sec) return `${main}, ${sec}`.trim();
  if (main) return String(main).trim();
  return "";
}

function mapSuggestions(suggestions) {
  return (suggestions || [])
    .map((s, i) => {
      const p = s.placePrediction;
      const text = displayTextFromPlacePrediction(p);
      const id =
        (p?.placeId && String(p.placeId).trim()) ||
        (typeof p?.place === "string" && p.place.startsWith("places/")
          ? p.place.slice("places/".length).trim()
          : "") ||
        `s-${i}`;
      return text ? { id, text } : null;
    })
    .filter(Boolean);
}

function suggestionMentionsDeniaRaw(s) {
  const t = displayTextFromPlacePrediction(s?.placePrediction);
  return /(?:^|[,\s])(dénia|denia)\b/i.test(t);
}

function mergeLocalFirstRaw(local, primary) {
  const seen = new Set();
  const out = [];
  function add(s) {
    const p = s?.placePrediction;
    if (!p) return;
    const pid =
      (p.placeId && String(p.placeId).trim()) ||
      (typeof p.place === "string" && p.place.startsWith("places/")
        ? p.place.slice("places/".length).trim()
        : "");
    const key =
      (pid && String(pid)) ||
      displayTextFromPlacePrediction(p).toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(s);
  }
  for (const s of local || []) add(s);
  for (const s of primary || []) add(s);
  return out;
}

async function googleAutocompleteJson(apiKey, body) {
  const res = await fetch(URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": apiKey,
      "X-Goog-FieldMask":
        "suggestions.placePrediction.text,suggestions.placePrediction.placeId,suggestions.placePrediction.place",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    throw new Error(`${res.status} ${errText}`);
  }
  return res.json();
}

async function fetchViaBackendProxy(input, sessionToken, biasCenter, localityOpts = null) {
  try {
    const payload = {
      input,
      sessionToken,
      biasLat: biasCenter.latitude,
      biasLng: biasCenter.longitude,
    };
    if (
      localityOpts?.restrict &&
      localityOpts.lat != null &&
      localityOpts.lng != null
    ) {
      payload.localityLat = localityOpts.lat;
      payload.localityLng = localityOpts.lng;
      if (localityOpts.radiusM > 0) payload.localityRadiusM = localityOpts.radiusM;
    }
    const { data } = await api.post("/geo/places-autocomplete", payload);
    return mapSuggestions(data?.suggestions);
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[places:autocomplete:proxy]", err.response?.status, err.response?.data);
    }
    return [];
  }
}

/**
 * @param {string} input
 * @param {string} sessionToken UUID
 * @param {{ localityCenter?: { latitude: number, longitude: number }, localityRadiusM?: number }|undefined} opts
 *   Si `localityCenter` está definido, se usa `locationRestriction` a esa población (calles locales).
 * @returns {Promise<{ id: string, text: string }[]>}
 */
export async function fetchPlaceAutocompleteSuggestions(input, sessionToken, opts = {}) {
  const q = (input || "").trim();
  if (!q) return [];

  const localityCenter = opts?.localityCenter;
  const localityRadiusM =
    Number(opts?.localityRadiusM) > 0
      ? Number(opts.localityRadiusM)
      : PLACES_LOCALITY_RESTRICTION_RADIUS_M;
  const restrictLocal =
    localityCenter &&
    Number.isFinite(localityCenter.latitude) &&
    Number.isFinite(localityCenter.longitude);

  if (restrictLocal) {
    const common = {
      input: q,
      sessionToken,
      includedRegionCodes: INCLUDED_REGION_CODES,
      regionCode: "es",
      languageCode: "es",
    };
    const restrictionBody = {
      ...common,
      locationBias: {
        circle: {
          center: {
            latitude: localityCenter.latitude,
            longitude: localityCenter.longitude,
          },
          radius: localityRadiusM,
        },
      },
    };
    const apiKey = getPlacesApiKey();
    if (apiKey) {
      try {
        const dataLoc = await googleAutocompleteJson(apiKey, restrictionBody);
        const mapped = mapSuggestions(dataLoc.suggestions || []);
        if (mapped.length) return mapped;
      } catch (e) {
        console.warn("[places:autocomplete:locality]", e?.message || e);
      }
      // Autocomplete vacío/error → proxy (Text Search tipo Maps, tolera typos)
    }
    return fetchViaBackendProxy(q, sessionToken, localityCenter, {
      restrict: true,
      lat: localityCenter.latitude,
      lng: localityCenter.longitude,
      radiusM: localityRadiusM,
    });
  }

  const biasCenter = await resolveMapsLikeBiasCenter();
  const nearOffice =
    distKm(biasCenter, PLACES_HOME_CENTER) < PLACES_NEAR_OFFICE_KM;
  const locationBiasCircle = {
    circle: {
      center: biasCenter,
      radius: PLACES_MAPS_LIKE_BIAS_RADIUS_M,
    },
  };

  const apiKey = getPlacesApiKey();
  if (apiKey) {
    try {
      const common = {
        input: q,
        sessionToken,
        includedRegionCodes: INCLUDED_REGION_CODES,
        regionCode: "es",
        languageCode: "es",
      };
      const data1 = await googleAutocompleteJson(apiKey, {
        ...common,
        locationBias: locationBiasCircle,
      });
      let raw = data1.suggestions || [];
      const skipLocalBoost =
        /\b(madrid|barcelona|bilbao|sevilla|zaragoza|málaga|malaga|murcia|london|paris|parís|berlin|berlín|lisboa|amsterdam|milán|milano|roma|porto|dublin|bruselas)\b/i.test(
          q,
        );
      const shouldTryLocal =
        nearOffice &&
        !skipLocalBoost &&
        q.length >= 3 &&
        q.length <= 40 &&
        !raw.some(suggestionMentionsDeniaRaw);
      if (shouldTryLocal && PLACES_LOCAL_RESTRICTION_RADIUS_M > 0) {
        try {
          const data2 = await googleAutocompleteJson(apiKey, {
            ...common,
            locationRestriction: {
              circle: {
                center: biasCenter,
                radius: PLACES_LOCAL_RESTRICTION_RADIUS_M,
              },
            },
          });
          const localRaw = data2.suggestions || [];
          if (localRaw.length) raw = mergeLocalFirstRaw(localRaw, raw);
        } catch (e2) {
          console.warn("[places:autocomplete:local]", e2?.message || e2);
        }
      }
      const mapped = mapSuggestions(raw);
      if (mapped.length) return mapped;
    } catch (e) {
      console.warn("[places:autocomplete]", e?.message || e);
    }
  }

  return fetchViaBackendProxy(q, sessionToken, biasCenter);
}

export function newSessionToken() {
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const a = new Uint8Array(16);
    crypto.getRandomValues(a);
    return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  return `st${Date.now().toString(16)}${Math.random().toString(16).slice(2, 10)}`.slice(0, 32);
}

/**
 * Detalle de lugar tras elegir una sugerencia (población, provincia, CP, calle/número).
 * Siempre vía backend `/geo/places-details` (clave de servidor, sin CORS ni restricciones de referrer del navegador).
 * @param {string} placeId
 * @param {string} [_sessionToken] reservado; no se envía a Google en GET para evitar INVALID_ARGUMENT por formato.
 * @returns {Promise<{ calle: string, numero: string, poblacion: string, provincia: string, codigo_postal: string, pais?: string, pais_codigo?: string }|null>}
 */
export async function fetchPlaceAddressDetails(placeId, _sessionToken) {
  let pid = String(placeId || "").trim();
  if (pid.startsWith("places/")) pid = pid.slice("places/".length);
  if (!pid || pid.length > 280) return null;
  try {
    const { data } = await api.post("/geo/places-details", {
      placeId: pid,
    });
    if (!data || !data.ok) return null;
    return {
      calle: data.calle || "",
      numero: data.numero || "",
      portal: data.portal || "",
      puerta: data.puerta || "",
      poblacion: data.poblacion || "",
      poblacion_candidatos: Array.isArray(data.poblacion_candidatos)
        ? data.poblacion_candidatos
        : [],
      barrio: data.barrio || "",
      provincia: data.provincia || "",
      codigo_postal: data.codigo_postal || "",
      pais: data.pais || "",
      pais_codigo: data.pais_codigo || "",
      direccion: data.direccion || "",
      latitud: data.latitud ?? null,
      longitud: data.longitud ?? null,
    };
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[places:details:proxy]", err.response?.status, err.response?.data);
    }
    return null;
  }
}
